<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { defaultGraphQLClient } from '@/composables/default-urql-client'
import Add from '@/modules/links/modals/Add.vue'
import Edit from '@/modules/links/modals/Edit.vue'
import { useDialogStore } from '@/stores/dialog'
import { ExternalLinkIcon, Pencil1Icon, PlusIcon, TrashIcon, UpdateIcon } from '@radix-icons/vue'
import gql from 'graphql-tag'
import { ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Loader } from 'lucide-vue-next'

const props = defineProps<{
    active: boolean
}>()
const { confirm } = useDialogStore()
const items = ref([])
const showAddModal = ref(false)
const showEditModal = ref(false)
const loading = ref(false)
const uuid = ref(null)

watch(
    () => props.active,
    async () => {
        if (props.active) {
            await init()
        }
    }
)

async function init() {
    try {
        loading.value = true

        const {
            data: { links },
        } = await defaultGraphQLClient.query(
            gql`
                query {
                    links {
                        uuid
                        title
                        url
                    }
                }
            `,
            {
                sort: [
                    {
                        column: 'title',
                        direction: 'asc',
                    },
                ],
            }
        )

        items.value = links
    } finally {
        loading.value = false
    }
}

async function hardDelete(link) {
    try {
        await confirm({
            title: 'Hard delete link',
            message: `Are you sure you want to hard delete <b>${link.title}</b> link?`,
            agreeButtonText: 'Yes, hard delete it!',
            agreeButtonVariant: 'destructive',
        })

        const { error } = await defaultGraphQLClient.mutation(
            gql`
                mutation hardDeleteLink($uuid: String!) {
                    hardDeleteLink(uuid: $uuid)
                }
            `,
            {
                uuid: link.uuid,
            }
        )

        if (!error) {
            toast.success('Link hard deleted', {
                description: `Link ${link.title} has been hard deleted.`,
            })
            init()
        }
    } catch (e) {
        console.log(e)
    }
}

function edit(link) {
    showEditModal.value = !showEditModal.value
    uuid.value = link.uuid
}
</script>

<template>
    <AccordionItem value="links" class="border-b-0">
        <AccordionTrigger
            class="group py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800"
        >
            <div class="flex items-center justify-between flex-1">
                <div class="flex items-center">
                    <div class="w-6">
                        <FontAwesomeIcon :icon="['fal', 'external-link-square']" class="size-4!" />
                    </div>
                    <div class="ml-3 text-xs">Links</div>
                </div>
                <div class="flex items-center">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    variant="icon"
                                    size="icon-xs"
                                    class="h-4 w-4 mr-2 hidden group-hover:flex"
                                    @click.stop="showAddModal = true"
                                >
                                    <PlusIcon class="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Create link</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    variant="icon"
                                    size="icon-xs"
                                    class="h-4 w-4 mr-2 hidden group-hover:flex"
                                    @click.stop="init"
                                >
                                    <UpdateIcon
                                        class="h-3 w-3"
                                        :class="{ 'animate-spin': loading }"
                                    />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Refresh</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs">
            <div class="flex flex-col text-white">
                <div v-if="loading" class="flex items-center space-x-2 pl-[52px] py-2">
                    <Loader class="mr-2 h-4 w-4 shrink-0 opacity-50 animate-spin" />
                    <span>Loading...</span>
                </div>
                <template v-for="link in items" v-else :key="link.uuid">
                    <div
                        class="group flex items-center justify-between hover:bg-neutral-600/50 pl-[52px] py-2 pr-2"
                    >
                        <a :href="link.url" target="_blank" class="truncate">{{ link.title }}</a>
                        <div class="flex items-center">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <Button
                                            variant="icon"
                                            size="icon-xs"
                                            class="mr-2 invisible group-hover:visible"
                                            :href="link.url"
                                            as="a"
                                            target="_blank"
                                        >
                                            <ExternalLinkIcon class="h-3 w-3" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Open in new tab</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <Button
                                            variant="icon"
                                            size="icon-xs"
                                            class="mr-2 hidden group-hover:flex"
                                            @click.stop="edit(link)"
                                        >
                                            <Pencil1Icon class="h-3 w-3" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Edit link</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <Button
                                            variant="icon"
                                            size="icon-xs"
                                            class="mr-2 hidden group-hover:flex"
                                            @click.stop="hardDelete(link)"
                                        >
                                            <TrashIcon class="h-3 w-3" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Hard delete link</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </template>
            </div>
        </AccordionContent>
        <Add v-model="showAddModal" @success="init" />
        <Edit v-model="showEditModal" :uuid="uuid" @success="init" />
    </AccordionItem>
</template>
